import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";

export function ClientFees({ caseId, onChanged }: { caseId: string; onChanged: () => void }) {
  const [info, setInfo] = useState<any>(null);
  const [count, setCount] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = (d: any) => { setInfo(d); setCount(String(d.override ?? d.auto_count)); };
  useEffect(() => { api.get(`/admin/cases/${caseId}/client-fees`).then((r) => apply(r.data)).catch(() => setInfo(null)); }, [caseId]);
  if (!info || (!info.county_fee && !info.civil_fee)) return null;

  const save = async (value: number | null) => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/cases/${caseId}/county-fee-count`, { count: value });
      apply(r.data); toast.success(value === null ? "Back to automatic count" : "County count updated"); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const n = info.override ?? info.auto_count;
  return (
    <div className="space-y-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid="client-fees">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">This client's set fees</p>
      {info.county_fee > 0 && (
        <div className="space-y-2" data-testid="county-fee-section">
          <p className="text-sm text-[#1A2536]">Additional county/state: <b>{money(info.county_fee)}</b> each</p>
          <p className="text-xs text-[#64748B]" data-testid="county-fee-places">
            {info.places.length ? `Found outside Delaware (last 10 years): ${info.places.join("; ")}` : "No addresses outside Delaware in the last 10 years."}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm">Counties charged
              <input type="number" min={0} max={100} className="field-input ml-2 inline-block h-9 w-20 text-sm" value={count} onChange={(e) => setCount(e.target.value)} data-testid="county-fee-count" />
            </label>
            <button className="btn-secondary btn-sm h-9" disabled={busy || count === "" || Number(count) === n} onClick={() => save(Math.max(0, Math.floor(Number(count))))} data-testid="county-fee-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
            {info.override !== null && info.override !== undefined && (
              <button className="inline-flex items-center gap-1 text-xs font-medium text-[#1B365D]" disabled={busy} onClick={() => save(null)} data-testid="county-fee-reset">
                <RotateCcw className="h-3 w-3" /> Use automatic count ({info.auto_count})
              </button>
            )}
            <span className="ml-auto font-mono text-sm font-semibold" data-testid="county-fee-total">{money(n * info.county_fee)}</span>
          </div>
          {info.override !== null && info.override !== undefined && <p className="text-xs text-[#C2410C]" data-testid="county-fee-adjusted">Adjusted by staff (automatic count is {info.auto_count}).</p>}
        </div>
      )}
      {info.civil_fee > 0 && (
        <div className="flex justify-between text-sm" data-testid="civil-fee-section"><span>Civil search</span><span className="font-mono font-semibold" data-testid="civil-fee-amount">{money(info.civil_fee)}</span></div>
      )}
    </div>
  );
}
